import React from 'react'
import { Link } from 'react-router-dom'
import { useSeo } from '../../lib/i18n'

export default function TeoriUndervisningEn() {
  useSeo({
    title: 'Theory & Lessons | Borgs Driving School Kalundborg',
    description: 'Engaging, interactive theory lessons at Borgs Driving School I/S in Kalundborg. Interactive board teaching, flexible teams, and high pass rates.',
    lang: 'en',
    pathname: '/en/theory-classes'
  })

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Theory Lessons in Kalundborg
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            At Borgs Driving School I/S, theory lessons are vivid, interactive, and personal. We go through all traffic rules at a calm pace where there is always room for questions.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-chalkboard-user text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Classroom Teaching &amp; Dialogue
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              We combine modern board teaching with active dialogue. You are not left sitting alone in front of a screen — the instructor ensures everyone grasps the core theory.
            </p>
          </div>

          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-laptop-code text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Online Theory Test Access
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              As a student with us, you receive free online access to practice theory tests, so you can practice at home on your phone, tablet, or PC.
            </p>
          </div>

          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-calendar-days text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Flexible Team Schedules
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              New theory courses start regularly. Lessons are typically held in the late afternoon/evening, making it convenient alongside school or work.
            </p>
          </div>
        </div>

        {/* Instruction Section */}
        <div className="bg-white p-8 sm:p-12 border border-slate-200 shadow-sm mb-16 space-y-6">
          <div>
            <h2 className="font-header text-3xl font-extrabold text-slate-900">
              Instruction Structure
            </h2>
            <p className="mt-2 text-slate-800 font-bold text-base">
              New courses normally start every 5 weeks, alternating between Monday and Wednesday. You can see the next class start on the front page.
            </p>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              A new course normally begins every 5 weeks. This means that if for any reason you need to pause or postpone, you can resume your driver education within 5 weeks.
            </p>

            <p>
              Please note that in most cases you cannot pause for more than 3 months without having to restart according to statutory rules.
            </p>

            <section className="pt-4 border-t border-slate-200 space-y-2">
              <h3 className="font-header text-xl font-bold text-slate-900">
                Theory lessons take place as classroom board teaching or PC-based instruction. This is optional. See more under Enrolment &gt; Getting Started.
              </h3>
              <p>
                There is always opportunity to ask about anything you don't understand, and you are provided home access codes to practice theory tests.
              </p>
              <p>
                Before and after compulsory lessons at the school, you can take practice theory tests on the large screen.
              </p>
              <p>
                This provides the major advantage of reviewing questions collectively, gaining comprehensive explanations, and learning together so you avoid making the same mistakes during the official test.
              </p>
            </section>

            <section className="pt-4 border-t border-slate-200 space-y-2">
              <h3 className="font-header text-xl font-bold text-slate-900">
                In-car driving lessons are arranged individually
              </h3>
              <p>
                For night driving lessons, you will be picked up at home and dropped off at home (unless you request otherwise).
              </p>
              <p>
                Manoeuvre track takes place at Rynkevangen 11 here in Kalundborg.
              </p>
              <p>
                Driving technique (skid track) takes place in Sorø.
              </p>
              <p>
                Dates are scheduled individually in agreement between the student, instructor, and the track facility.
              </p>
            </section>
          </div>
        </div>

        {/* Theory Location Section */}
        <div className="bg-white p-8 sm:p-12 border border-slate-200 shadow-sm mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <h2 className="font-header text-3xl font-bold text-slate-900">
                Elmegade 19, 4400 Kalundborg
              </h2>
              <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                Our modern theory classroom is conveniently located in Kalundborg with easy access by public transport and bicycle.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/en/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm shadow-sm transition-colors"
                >
                  <span>Sign up for next theory course</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            <div className="border border-slate-200 p-2 bg-slate-50">
              <div className="w-full h-64 bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm">
                <div className="text-center p-4">
                  <i className="fa-solid fa-location-dot text-emerald-600 text-3xl mb-2 block" aria-hidden="true"></i>
                  <span>Borgs Driving School I/S</span>
                  <p className="text-xs text-slate-600 font-normal mt-1">Elmegade 19, 4400 Kalundborg</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
